/**
 * Time Clock data layer. Uses the real Time Clock API (time-entries).
 * Backend may require X-Tenant-ID header; pass tenantId for all write/read calls.
 */

import { createAuthenticatedApi } from "@/lib/utils";
import { runStaffReviewAction } from "@/lib/review-concurrency/run-staff-review-action";
import { resolveEffectiveTenantNumber } from "@/lib/utils/env/effective-tenant";
import {
  isSentinelCalendarYmd,
  normalizeTimeEntryDateYmd,
  resolveTimeEntrySubmitDateYmd,
  timeEntryYmdToApiDate,
} from "@/lib/utils/date/time-entry-date";
import { TimeClockApi } from "@/openapi/generated";
import type {
  CreateTimeEntryRequestAppointmentId,
  GetTimeEntryRequest,
  GetTimeEntryResponse,
  PaginatedResultOfGetTimeEntryResponse,
} from "@/openapi/generated/models";

const DEFAULT_TENANT_ID = resolveEffectiveTenantNumber();

/** UI-facing time clock entry (maps from API GetTimeEntryResponse). */
export interface TimeClockEntry {
  id: string;
  userId: number;
  date: string; // YYYY-MM-DD
  startTime: string; // HH:mm
  endTime: string;
  /** Display name from timeEntryType (e.g. Regular, Private Lesson, Sick Leave). */
  entryType?: string;
  /** API time entry type id; used when creating/updating. */
  timeEntryTypeId?: number | null;
  /** Linked private appointment (when time entry type is private lesson). */
  appointmentId?: number | null;
  /** Class / private lesson name when API returns it (or from form; refetch may replace). */
  appointmentName?: string | null;
  payRate?: number;
  notes: string | null;
  /** Whether this entry has been approved for payroll. */
  isApproved?: boolean | null;
  createdAt: string;
  updatedAt: string;
}

const DEFAULT_PAGE_SIZE = 500;
export const TIME_CLOCK_SCROLL_PAGE_SIZE = 500;

export interface ScrollTimeClockEntriesParams {
  page: number;
  pageSize?: number;
  lastSelectedId?: string;
  request: GetTimeEntryRequest;
}

export async function scrollTimeClockEntries(
  accessToken: string,
  params: ScrollTimeClockEntriesParams,
  tenantId: number = DEFAULT_TENANT_ID,
): Promise<PaginatedResultOfGetTimeEntryResponse> {
  const api = createAuthenticatedApi(TimeClockApi, accessToken);
  return api.apiTimeEntriesScrollPost({
    getTimeEntryRequest: params.request,
    page: params.page,
    pageSize: params.pageSize ?? TIME_CLOCK_SCROLL_PAGE_SIZE,
    lastSelectedId: params.lastSelectedId,
    xTenantID: tenantId,
  });
}

type TimeEntryWithAppointment = GetTimeEntryResponse & {
  appointment?: {
    name?: string;
    appointmentName?: string;
    appointmentId?: number;
  };
  appointmentName?: string;
};

/**
 * How the time-entry type column should read for the table: includes class name
 * for private lessons when `appointmentName` (or at least `appointmentId`) is present.
 */
export function formatTimeEntryTypeLabel(entry: TimeClockEntry): string {
  const type = entry.entryType?.trim() || "-";
  const isPrivateLesson = /private/i.test(type) && /lesson/i.test(type);
  if (!isPrivateLesson) return type;
  const name = entry.appointmentName?.trim();
  if (name) return `${type} - ${name}`;
  if (entry.appointmentId != null && entry.appointmentId > 0) {
    return `${type} (#${entry.appointmentId})`;
  }
  return type;
}

function parseAppointmentFromTimeEntry(res: GetTimeEntryResponse): {
  id: number | null;
  name: string | null;
} {
  const ext = res as TimeEntryWithAppointment;
  if (ext.appointment?.name?.trim() || ext.appointment?.appointmentName) {
    const n = ext.appointment.appointmentId;
    return {
      id: typeof n === "number" && Number.isFinite(n) ? n : null,
      name:
        ext.appointment.name?.trim() ||
        (ext.appointment.appointmentName != null
          ? String(ext.appointment.appointmentName).trim()
          : null) ||
        null,
    };
  }
  const fromTop = ext.appointmentName?.trim();
  const raw = res.appointmentId as unknown;
  if (raw == null) {
    return { id: null, name: fromTop || null };
  }
  if (typeof raw === "number") {
    return { id: Number.isFinite(raw) ? raw : null, name: fromTop || null };
  }
  if (typeof raw === "string" && /^\d+$/.test(raw)) {
    return { id: Number(raw), name: fromTop || null };
  }
  if (typeof raw === "object" && raw !== null) {
    const o = raw as Record<string, unknown>;
    const id = o.appointmentId ?? o.id;
    const name = o.name ?? o.appointmentName;
    const idNum = typeof id === "number" ? id : id != null ? Number(id) : null;
    return {
      id: idNum != null && Number.isFinite(idNum) ? idNum : null,
      name: name != null ? String(name).trim() : fromTop || null,
    };
  }
  return { id: null, name: fromTop || null };
}

function toApiAppointmentId(
  id: number | null | undefined,
): CreateTimeEntryRequestAppointmentId | null {
  if (id == null || !Number.isFinite(id) || id <= 0) {
    return null;
  }
  return id as unknown as CreateTimeEntryRequestAppointmentId;
}

/** API expects time as HH:mm:ss; form gives HH:mm. Normalize so backend accepts. */
function toApiTime(hhmm: string): string {
  if (!hhmm || typeof hhmm !== "string") return "00:00:00";
  const trimmed = hhmm.trim();
  if (/^\d{1,2}:\d{2}:\d{2}$/.test(trimmed)) return trimmed;
  if (/^\d{1,2}:\d{2}$/.test(trimmed)) return `${trimmed}:00`;
  return trimmed;
}

/** Map API response item to UI TimeClockEntry. Exported for payroll approved-entries API. */
export function mapResponseToEntry(res: GetTimeEntryResponse): TimeClockEntry {
  const createdAt =
    res.createdAt instanceof Date ? res.createdAt : new Date(res.createdAt);
  const updatedAt =
    res.updatedAt instanceof Date
      ? res.updatedAt
      : res.updatedAt
        ? new Date(res.updatedAt)
        : createdAt;
  const timeEntryTypeId =
    res.timeEntryType != null ? Number(res.timeEntryType.id) : null;
  const entryType = res.timeEntryType?.name ?? "Regular";
  const { id: apptId, name: apptName } = parseAppointmentFromTimeEntry(res);
  return {
    id: res.id,
    userId: Number(res.userId),
    date: normalizeTimeEntryDateYmd(res.entryDate),
    startTime: res.clockIn,
    endTime: res.clockOut,
    entryType,
    timeEntryTypeId: timeEntryTypeId ?? undefined,
    appointmentId: apptId,
    appointmentName: apptName,
    notes: res.remarks ?? null,
    isApproved: res.isApproved ?? undefined,
    createdAt: createdAt.toISOString(),
    updatedAt: updatedAt.toISOString(),
  };
}

export async function getTimeClockEntries(
  accessToken: string,
  userId: number,
  tenantId: number = DEFAULT_TENANT_ID,
): Promise<TimeClockEntry[]> {
  const api = createAuthenticatedApi(TimeClockApi, accessToken);
  const result = await api.apiTimeEntriesScrollPost({
    getTimeEntryRequest: { userId },
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    xTenantID: tenantId,
  });
  const items = result.items ?? [];
  const entries = items.map(mapResponseToEntry);
  entries.sort((a, b) => {
    const d = b.date.localeCompare(a.date);
    if (d !== 0) return d;
    return b.startTime.localeCompare(a.startTime);
  });
  return entries;
}

/** Fetches time entries for multiple users (or all if userIds empty). Omitting userId in request returns all entries from backend. */
export async function getTimeClockEntriesAll(
  accessToken: string,
  userIds: number[],
  tenantId: number = DEFAULT_TENANT_ID,
): Promise<TimeClockEntry[]> {
  const api = createAuthenticatedApi(TimeClockApi, accessToken);
  const result = await api.apiTimeEntriesScrollPost({
    getTimeEntryRequest: {},
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    xTenantID: tenantId,
  });
  let items = result.items ?? [];
  if (userIds.length > 0) {
    const set = new Set(userIds);
    items = items.filter((item) => set.has(Number(item.userId)));
  }
  const allEntries = items.map(mapResponseToEntry);
  allEntries.sort((a, b) => {
    const d = b.date.localeCompare(a.date);
    if (d !== 0) return d;
    return b.startTime.localeCompare(a.startTime);
  });
  return allEntries;
}

function entryDateStringToDate(dateYyyyMmDd: string): Date {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateYyyyMmDd.trim());
  if (!m) return new Date(dateYyyyMmDd);
  return timeEntryYmdToApiDate(dateYyyyMmDd.trim());
}

export async function addTimeClockEntry(
  accessToken: string,
  userId: number,
  payload: Omit<TimeClockEntry, "id" | "userId" | "createdAt" | "updatedAt">,
  tenantId: number = DEFAULT_TENANT_ID,
): Promise<void> {
  const api = createAuthenticatedApi(TimeClockApi, accessToken);
  await api.apiTimeEntriesPost({
    createTimeEntryRequest: {
      userId,
      entryDate: entryDateStringToDate(payload.date),
      clockIn: toApiTime(payload.startTime),
      clockOut: toApiTime(payload.endTime),
      timeEntryTypeId: payload.timeEntryTypeId ?? null,
      remarks: payload.notes ?? null,
      appointmentId: toApiAppointmentId(
        payload.appointmentId == null ? null : Number(payload.appointmentId),
      ),
    },
    xTenantID: tenantId,
  });
}

export async function updateTimeClockEntry(
  accessToken: string,
  id: string,
  _userId: number,
  payload: Partial<
    Pick<
      TimeClockEntry,
      | "date"
      | "startTime"
      | "endTime"
      | "notes"
      | "timeEntryTypeId"
      | "appointmentId"
    >
  >,
  tenantId: number = DEFAULT_TENANT_ID,
): Promise<void> {
  await runStaffReviewAction(async () => {
  const api = createAuthenticatedApi(TimeClockApi, accessToken);
  const existing = await api.apiTimeEntriesIdGet({ id, xTenantID: tenantId });
  const timeEntryTypeId =
    payload.timeEntryTypeId !== undefined
      ? payload.timeEntryTypeId
      : existing.timeEntryType != null
        ? Number(existing.timeEntryType.id)
        : null;
  const { id: existingApptId } = parseAppointmentFromTimeEntry(existing);
  const nextAppointmentId =
    payload.appointmentId !== undefined
      ? payload.appointmentId
      : existingApptId;
  const existingDateYmd = normalizeTimeEntryDateYmd(existing.entryDate);
  const nextDateYmd = resolveTimeEntrySubmitDateYmd(
    payload.date ?? "",
    existingDateYmd,
  );
  if (!nextDateYmd || isSentinelCalendarYmd(nextDateYmd)) {
    throw new Error("A valid entry date is required to update this time entry.");
  }
  const entryDate = entryDateStringToDate(nextDateYmd);

  await api.apiTimeEntriesIdPut({
    id,
    updateTimeEntryRequest: {
      id,
      entryDate,
      clockIn:
        payload.startTime != null
          ? toApiTime(payload.startTime)
          : existing.clockIn,
      clockOut:
        payload.endTime != null
          ? toApiTime(payload.endTime)
          : existing.clockOut,
      timeEntryTypeId,
      remarks: payload.notes !== undefined ? payload.notes : existing.remarks,
      appointmentId: toApiAppointmentId(
        nextAppointmentId == null ? null : Number(nextAppointmentId),
      ),
    },
    xTenantID: tenantId,
  });
  });
}

export async function deleteTimeClockEntry(
  accessToken: string,
  id: string,
  _userId: number,
  tenantId: number = DEFAULT_TENANT_ID,
): Promise<void> {
  await runStaffReviewAction(async () => {
    const api = createAuthenticatedApi(TimeClockApi, accessToken);
    await api.apiTimeEntriesIdDelete({ id, xTenantID: tenantId });
  });
}
